import { ChangeDetectionStrategy, ChangeDetectorRef, Component, Directive, HostListener, Input, OnInit, ViewEncapsulation } from "@angular/core";
import { IndexSummaryTableService } from "./index-summary-table.service";
import { tap } from "rxjs";
import { SortDirection } from "./sort-direction";

export interface ColSort {
    column: string,
    state: SortDirection,
}

@Directive({
    selector: '[sortableColumn]',
    host: {
        '[class.cursor-pointer]': 'isEnabled()',
        '[attr.tabindex]': 'isEnabled() ? "0" : null',
    }
})
export class SortableColumn implements OnInit {

    @Input("sortableColumn") field: string;

    @Input() sortableColumnDisabled: boolean;

    state: SortDirection = SortDirection.INACTIVE;

    constructor(public tableService: IndexSummaryTableService) {
    }

    ngOnInit() { }

    @HostListener('click', ['$event'])
    onClick(event: MouseEvent) {
        if (this.isEnabled()) {
            this.sort();
        }
    }

    @HostListener('keydown.enter', ['$event'])
    onEnterKey(event: MouseEvent) {
        this.onClick(event);
    }

    isEnabled(): boolean {
        return this.sortableColumnDisabled !== true;
    }

    sort(): void {
        this.state = this.toggle(this.state);
        this.tableService.onSort({ column: this.field, state: this.state });
    }

    toggle(currentState: SortDirection): SortDirection {
        switch (currentState) {
            case SortDirection.INACTIVE:
                return SortDirection.DESCENDING;
            case SortDirection.DESCENDING:
                return SortDirection.ASCENDING;
            case SortDirection.ASCENDING:
                return SortDirection.DESCENDING;
        }
    }
}

@Component({
    selector: 'sortIcon',
    template: `
    <!-- Heroicon name: chevron-down -->
    <svg *ngIf="isDescending" xmlns="http://www.w3.org/2000/svg" class="h-5 w-5 text-blue-500" fill="none"
        viewBox="0 0 20 20" fill="currentColor" aria-hidden="true">
        <path fill-rule="evenodd"
            d="M5.293 7.293a1 1 0 011.414 0L10 10.586l3.293-3.293a1 1 0 111.414 1.414l-4 4a1 1 0 01-1.414 0l-4-4a1 1 0 010-1.414z"
            clip-rule="evenodd" />
    </svg>

    <!-- Heroicon name: chevron-up -->
    <svg *ngIf="isAscending" xmlns="http://www.w3.org/2000/svg" class="h-5 w-5 text-blue-500" viewBox="0 0 20 20" fill="currentColor">
        <path fill-rule="evenodd"
            d="M14.707 12.707a1 1 0 01-1.414 0L10 9.414l-3.293 3.293a1 1 0 01-1.414-1.414l4-4a1 1 0 011.414 0l4 4a1 1 0 010 1.414z"
            clip-rule="evenodd" />
    </svg>

    <!-- Heroicon name: chevron-down -->
    <svg *ngIf="isInactive" xmlns="http://www.w3.org/2000/svg" class="h-5 w-5" fill="none" viewBox="0 0 20 20"
        fill="currentColor" aria-hidden="true">
        <path fill-rule="evenodd"
            d="M5.293 7.293a1 1 0 011.414 0L10 10.586l3.293-3.293a1 1 0 111.414 1.414l-4 4a1 1 0 01-1.414 0l-4-4a1 1 0 010-1.414z"
            clip-rule="evenodd" />
    </svg>
    `,
    changeDetection: ChangeDetectionStrategy.OnPush,
    encapsulation: ViewEncapsulation.None,
})
export class SortIcon implements OnInit {

    @Input() field: string;

    state: SortDirection = SortDirection.INACTIVE;

    constructor(
        public tableService: IndexSummaryTableService,
        public cd: ChangeDetectorRef) {
    }

    ngOnInit() {
        this.tableService
            .activeSort$
            .pipe(
                tap(activeSort => {
                    this.setSortIconState(activeSort);
                })
            ).subscribe();
    }

    setSortIconState(colSort: ColSort) {
        if (colSort == null || colSort.column != this.field) {
            this.state = SortDirection.INACTIVE;
            this.cd.markForCheck();
            return;
        }

        if (colSort.column == this.field) {
            this.state = colSort.state;
            this.cd.markForCheck();
            return;
        }
    }

    get isDescending(): boolean {
        return this.state === SortDirection.DESCENDING;
    }

    get isAscending(): boolean {
        return this.state === SortDirection.ASCENDING;
    }

    get isInactive(): boolean {
        return this.state === SortDirection.INACTIVE
            || this.state === null;
    }
}